import React, { useRef, useState } from "react";
import { Upload, Loader2, X, LayoutGrid } from "lucide-react";
import toast from "react-hot-toast";

import { uploadProposalImage } from "../api/prospectingReportsApi";
import ImageLibraryModal from "./ImageLibraryModal";

const ACCEPT = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const MAX_BYTES = 8 * 1024 * 1024;
const field =
  "w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#55c5d0]/40";

/**
 * An image field with BOTH a URL paste box and an upload button. The same
 * validation the backend enforces (type: JPG/PNG/WEBP/GIF, size: <=8MB) is
 * checked client-side first for an instant, friendly error.
 *
 * Props: value (url string), onChange(url), label, placeholder, compact.
 */
const ImageUploadField = ({ value, onChange, label, placeholder = "https://…", compact = false }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);

  const pick = () => inputRef.current && inputRef.current.click();

  const onFile = async (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;
    if (!ACCEPT.includes(file.type)) {
      toast.error("Please choose a JPG, PNG, WEBP or GIF image.");
      return;
    }
    if (file.size > MAX_BYTES) {
      toast.error("That image is over the 8MB limit.");
      return;
    }
    setUploading(true);
    try {
      const { data } = await uploadProposalImage(file);
      onChange(data.url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className={compact ? "" : "mb-3"}>
      {label && <label className="block text-xs font-semibold text-gray-500 mb-1">{label}</label>}
      <div className="flex items-start gap-2">
        {value ? (
          <div className="relative shrink-0">
            <img src={value} alt="" className="w-14 h-14 rounded-lg object-cover border border-gray-200" />
            <button type="button" onClick={() => onChange("")}
              className="absolute -top-1.5 -right-1.5 bg-white rounded-full border border-gray-200 text-gray-400 hover:text-red-500"
              aria-label="Remove image">
              <X className="w-4 h-4" />
            </button>
          </div>
        ) : null}
        <div className="flex-1">
          <input className={field} value={value || ""} placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)} />
          <button type="button" onClick={pick} disabled={uploading}
            className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-[#0f8d9b] disabled:opacity-50">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? "Uploading…" : "Upload image"}
          </button>
          <button type="button" onClick={() => setLibraryOpen(true)}
            className="mt-1 ml-3 inline-flex items-center gap-1 text-sm font-semibold text-[#0f8d9b]">
            <LayoutGrid className="w-4 h-4" /> Choose from library
          </button>
          <p className="text-[11px] text-gray-400 mt-0.5">JPG, PNG, WEBP or GIF · up to 8MB</p>
        </div>
      </div>
      <input ref={inputRef} type="file" accept={ACCEPT.join(",")} className="hidden" onChange={onFile} />
      {libraryOpen && (
        <ImageLibraryModal onClose={() => setLibraryOpen(false)}
          onPick={(url) => { onChange(url); setLibraryOpen(false); }} />
      )}
    </div>
  );
};

export default ImageUploadField;
